import { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import QRCode from 'qrcode';
import {
  Box,
  Stack,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Iconify } from 'src/components/iconify';
import { getQrCodesForBranch } from 'src/api/qr-codes.api';
import { exportQrCodeAsPng } from 'src/utils/qr-code-generator';
import { generateQrCodePdf } from 'src/utils/pdf-generator';

// ----------------------------------------------------------------------

interface QrCodeItem {
  id: string;
  branchId: string;
  encryptedPayload: string;
  generatedAt: string;
  expiresAt: string | null;
  generatedBy: string;
  locationRangeMeters: number;
  isActive: boolean;
  isExpired: boolean;
}

interface BranchQrCodesTabProps {
  branchId: string;
  branchName: string;
  refreshKey?: number;
}

// ----------------------------------------------------------------------

export default function BranchQrCodesTab({ branchId, branchName, refreshKey }: BranchQrCodesTabProps) {
  const [qrCodes, setQrCodes] = useState<QrCodeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);

  const fetchQrCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getQrCodesForBranch(branchId);
      // Backend eski QR'ları deaktive etmiyor (rotasyon/invalidation yok) — şube başına
      // yalnız en son üretilen QR gösterilir; eskiler DB'de durur ve okutulmaya devam eder.
      const latest = [...data]
        .sort((a, b) => new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime())
        .slice(0, 1);
      setQrCodes(latest);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'QR kodları yüklenirken hata oluştu';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    fetchQrCodes();
  }, [fetchQrCodes, refreshKey]);

  const handleExportPng = async (qrCode: QrCodeItem) => {
    try {
      setExportingId(qrCode.id);
      await exportQrCodeAsPng(qrCode.encryptedPayload, branchName);
    } catch (err) {
      console.error('PNG export failed:', err);
    } finally {
      setExportingId(null);
    }
  };

  const handleExportPdf = async (qrCode: QrCodeItem) => {
    try {
      setExportingId(qrCode.id);
      const dataUrl = await QRCode.toDataURL(qrCode.encryptedPayload, {
        width: 512,
        margin: 2,
        errorCorrectionLevel: 'M',
        type: 'image/png',
      });

      const metadata = {
        id: qrCode.id,
        branchId: qrCode.branchId,
        branchName,
        branchAddress: '',
        latitude: 0,
        longitude: 0,
        locationRangeMeters: qrCode.locationRangeMeters,
        encryptedQrCode: qrCode.encryptedPayload,
        generatedAt: qrCode.generatedAt,
        expiresAt: qrCode.expiresAt || undefined,
        ttlHours: 0,
        isExpired: qrCode.isExpired,
        generatedBy: qrCode.generatedBy,
        generatedByName: qrCode.generatedBy,
        invalidated: false,
      };

      await generateQrCodePdf(metadata, dataUrl);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExportingId(null);
    }
  };

  const getStatusChip = (qrCode: QrCodeItem) => {
    if (qrCode.isExpired) {
      return <Chip label="Süresi Dolmuş" color="error" size="small" />;
    }
    if (!qrCode.isActive) {
      return <Chip label="Deaktif" color="default" size="small" />;
    }
    if (!qrCode.expiresAt) {
      return <Chip label="Süresiz" color="success" size="small" />;
    }
    return <Chip label="Aktif" color="success" size="small" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={fetchQrCodes}>
            Tekrar Dene
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (qrCodes.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Iconify icon="eva:file-remove-outline" width={64} sx={{ color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Henüz QR kod oluşturulmamış
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
          Yukarıdaki &quot;QR Kod Oluştur&quot; butonunu kullanarak yeni QR kod oluşturabilirsiniz.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h6">
          QR Kodları ({qrCodes.length})
        </Typography>
        <Button
          size="small"
          startIcon={<Iconify icon="eva:refresh-fill" />}
          onClick={fetchQrCodes}
        >
          Yenile
        </Button>
      </Stack>

      <Grid container spacing={2}>
        {qrCodes.map((qrCode) => (
          <Grid item xs={12} sm={6} md={4} key={qrCode.id}>
            <QrCodeCard
              qrCode={qrCode}
              branchName={branchName}
              isExporting={exportingId === qrCode.id}
              onExportPng={() => handleExportPng(qrCode)}
              onExportPdf={() => handleExportPdf(qrCode)}
              statusChip={getStatusChip(qrCode)}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ----------------------------------------------------------------------

interface QrCodeCardProps {
  qrCode: QrCodeItem;
  branchName: string;
  isExporting: boolean;
  onExportPng: () => void;
  onExportPdf: () => void;
  statusChip: React.ReactNode;
}

function QrCodeCard({
  qrCode,
  branchName,
  isExporting,
  onExportPng,
  onExportPdf,
  statusChip,
}: QrCodeCardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current && qrCode.encryptedPayload) {
      QRCode.toCanvas(canvasRef.current, qrCode.encryptedPayload, {
        width: 180,
        margin: 2,
        errorCorrectionLevel: 'M',
      });
    }
  }, [qrCode.encryptedPayload]);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2} alignItems="center">
          {/* QR Code Canvas */}
          <canvas ref={canvasRef} style={{ borderRadius: 8 }} />

          {/* Status */}
          {statusChip}

          {/* Metadata */}
          <Stack spacing={0.5} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Oluşturulma
              </Typography>
              <Typography variant="caption">
                {format(new Date(qrCode.generatedAt), 'dd/MM/yyyy HH:mm')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Son Kullanma
              </Typography>
              <Typography
                variant="caption"
                color={qrCode.isExpired ? 'error.main' : 'text.primary'}
              >
                {qrCode.expiresAt
                  ? format(new Date(qrCode.expiresAt), 'dd/MM/yyyy HH:mm')
                  : 'Süresiz'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">
                Konum Aralığı
              </Typography>
              <Typography variant="caption">
                {qrCode.locationRangeMeters} m
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ width: '100%' }} />

          {/* Export Buttons */}
          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                isExporting ? (
                  <CircularProgress size={16} />
                ) : (
                  <Iconify icon="eva:download-fill" />
                )
              }
              onClick={onExportPng}
              disabled={isExporting}
              fullWidth
            >
              PNG
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                isExporting ? (
                  <CircularProgress size={16} />
                ) : (
                  <Iconify icon="eva:file-text-fill" />
                )
              }
              onClick={onExportPdf}
              disabled={isExporting}
              fullWidth
            >
              PDF
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
